import { useState } from 'react'

import { AuthFactorTypes } from '$constants/auth'
import { ChoiceProps } from '$blocks/MFABlock/Choice'

import { useModal } from '$contexts/Modal'
import {
  ResponseResolver,
  With2FAGuardErrorResponse
} from '$services/api/types'

import useEmailOTP from './useEmailOTP'
import useSmsOTP from './useSmsOTP'
import useLarkOTP from './useLarkOTP'
import useAuthenticatorTOTP from './useAuthenticatorTOTP'
import usePasskey from './usePasskey'

import { FactorHookOptions, Token } from './types'

type DataProps = Pick<
  ResponseResolver<With2FAGuardErrorResponse>,
  'available_factors'
>

export type UseMFABlockOptions = FactorHookOptions<{
  data: DataProps
  onExited?: VoidFunction
  onCancelled?: (action: Promise<void>) => void
}>

export const useMFABlock = ({
  data,
  onExited,
  onVerified,
  onCancelled,
  ...restOptions
}: UseMFABlockOptions) => {
  const [selectedFactor, selectFactor] = useState<AuthFactorTypes>()
  const [
    /* Last MFA Token */
    mfa_token,
    cacheMFAToken
  ] = useState<Token>()

  const factorCtrlOptions: FactorHookOptions = {
    ...restOptions,
    onVerified: token => {
      cacheMFAToken(token)
      onVerified(token)
    }
  }

  const modalProps = useModal({
    disabledOverlayClick: true,
    onOverlayClick: () => onExit()
  })
  const { modalControls, modalControlsAsync } = modalProps

  const emailOTP = useEmailOTP(factorCtrlOptions)
  const smsOTP = useSmsOTP(factorCtrlOptions)
  const larkOTP = useLarkOTP(factorCtrlOptions)
  const authenticatorTOTP = useAuthenticatorTOTP(factorCtrlOptions)
  const passkey = usePasskey(factorCtrlOptions)

  const allFactorControllers = [
    emailOTP,
    smsOTP,
    authenticatorTOTP,
    larkOTP,
    passkey
  ]
  const factorControllers = {
    emailOTP,
    smsOTP,
    authenticatorTOTP,
    larkOTP,
    passkey
  }

  const disabled = allFactorControllers.some(({ isAnyLoading }) => isAnyLoading)

  const showChoices = !selectedFactor || selectedFactor === 'passkey'

  const resetFactorStates = () => {
    allFactorControllers.forEach(({ reset }) => {
      reset()
    })
  }
  const resetFactorSelection = () => {
    selectFactor(undefined)
  }
  const resetVerifiedCache = () => {
    cacheMFAToken(undefined)
  }

  const reset = () => {
    resetFactorSelection()
    resetVerifiedCache()
    resetFactorStates()
  }

  const exit = async (resetStates = true) => {
    await modalControlsAsync.close()

    if (resetStates) {
      reset()
    }

    onExited?.()
  }

  const onExit = () => {
    const action = exit()

    onCancelled?.(action)
  }

  const onBack = () => {
    if (selectedFactor) {
      reset()
      return
    }

    onExit()
  }

  const onSelect: ChoiceProps['onClick'] = ({ type: factor_type }) => {
    resetVerifiedCache()

    selectFactor(factor_type)

    switch (factor_type) {
      case 'email_otp':
        emailOTP.challenge({})

        break

      case 'sms_otp':
        smsOTP.challenge({})

        break

      case 'lark_otp':
        larkOTP.challenge({})

        break

      case 'passkey':
        passkey.authenticate()

        break
    }
  }

  const retakeSameChallenge = () => {
    switch (selectedFactor) {
      case 'email_otp':
        emailOTP.reChallenge()

        break

      case 'sms_otp':
        smsOTP.reChallenge()

        break

      case 'lark_otp':
        larkOTP.reChallenge()

        break
    }
  }

  const hasMoreThanOneFactor = data.available_factors.length > 1

  return {
    modalProps,
    modalControls,
    modalControlsAsync,
    data,
    hasMoreThanOneFactor,
    mfa_token,
    selectedFactor,
    factorControllers,
    showChoices,
    exit,
    reset,
    resetFactorStates,
    onSelect,
    onExit,
    onBack,
    disabled,
    retakeSameChallenge
  }
}

export default useMFABlock
